/**
 * Cache Coordination Service
 * 
 * Manages coordination between individual show caches and bulk shows cache
 * to eliminate cache fragmentation and improve consistency.
 * 
 * Features:
 * - Synchronizes individual and bulk caches
 * - Coordinates cache invalidation strategies
 * - Optimizes cache hit rates through better coordination
 * - Manages cache consistency across related queries
 * - Provides performance monitoring for cache operations
 */

import { QueryClient } from '@tanstack/react-query';
import { Show } from '../types/Show';
import { SHOWS_QUERY_KEY } from '../hooks/queries/useShowsQuery';
import { debugError, debugLog } from '../utils/debugLog';

export interface CacheCoordinationMetrics {
  bulkCacheHits: number;
  individualCacheHits: number;
  cacheMisses: number;
  coordinationEvents: number;
  lastCoordination: Date | null;
}

export class CacheCoordinationService {
  private queryClient: QueryClient;
  private metrics: CacheCoordinationMetrics;
  private coordinationListeners: Set<(event: CacheCoordinationEvent) => void>;

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
    this.metrics = {
      bulkCacheHits: 0,
      individualCacheHits: 0,
      cacheMisses: 0,
      coordinationEvents: 0,
      lastCoordination: null,
    };
    this.coordinationListeners = new Set();
  }

  /**
   * Synchronize individual show cache with bulk shows cache
   * Called when bulk shows cache is updated
   */
  public synchronizeIndividualCaches(updatedShows: Show[]): void {
    debugLog('🔄 Synchronizing individual show caches with bulk cache update');
    
    // Get all individual show query keys from cache
    const cache = this.queryClient.getQueryCache();
    const individualShowQueries = cache.getAll().filter(query => 
      Array.isArray(query.queryKey) && 
      query.queryKey.length === 2 && 
      query.queryKey[0] === 'show'
    );

    let synchronizedCount = 0;

    // Update each individual show cache with fresh data from bulk cache
    individualShowQueries.forEach(query => {
      const showId = query.queryKey[1] as string;
      const updatedShow = updatedShows.find(show => show.show_id === showId);
      
      if (updatedShow) {
        // Always update individual show cache with fresh data from bulk
        this.queryClient.setQueryData(['show', showId], updatedShow);
        synchronizedCount++;
        debugLog(`✅ Synchronized individual cache for show ${showId}`);
      } else {
        // Show exists in individual cache but not in bulk data - mark as orphaned
        debugLog(`⚠️ Show ${showId} exists in individual cache but not in bulk data`);
      }
    });

    // Update metrics and notify listeners
    this.metrics.coordinationEvents++;
    this.metrics.lastCoordination = new Date();
    
    this.notifyCoordinationEvent({
      type: 'bulk-to-individual-sync',
      showCount: synchronizedCount,
      timestamp: new Date(),
    });

    debugLog(`🎯 Cache coordination complete: ${synchronizedCount} individual caches synchronized`);
  }

  /**
   * Update bulk cache when individual show is updated
   * Called when individual show cache is updated
   */
  public updateBulkCacheFromIndividual(showId: string, updatedShow: Show): void {
    debugLog(`🔄 Updating bulk cache from individual show ${showId}`);
    
    // Get current bulk shows cache
    const currentShows = this.queryClient.getQueryData<Show[]>(SHOWS_QUERY_KEY);
    
    if (currentShows && Array.isArray(currentShows)) {
      // Update the specific show in bulk cache
      const updatedShows = currentShows.map(show => 
        show.show_id === showId ? updatedShow : show
      );
      
      // Check if show was actually found and updated
      const showFound = currentShows.some(show => show.show_id === showId);
      
      if (showFound) {
        // Update bulk cache with modified array
        this.queryClient.setQueryData(SHOWS_QUERY_KEY, updatedShows);
        debugLog(`✅ Updated bulk cache with changes from show ${showId}`);
        
        this.notifyCoordinationEvent({
          type: 'individual-to-bulk-sync',
          showId,
          timestamp: new Date(),
        });
      } else {
        // Show not found in bulk cache - add it
        const newBulkShows = [...currentShows, updatedShow];
        this.queryClient.setQueryData(SHOWS_QUERY_KEY, newBulkShows);
        debugLog(`✅ Added show ${showId} to bulk cache`);
        
        this.notifyCoordinationEvent({
          type: 'individual-to-bulk-sync',
          showId,
          timestamp: new Date(),
        });
      }
    } else {
      // No bulk cache exists - create it with this show
      this.queryClient.setQueryData(SHOWS_QUERY_KEY, [updatedShow]);
      debugLog(`✅ Created bulk cache with show ${showId}`);
      
      this.notifyCoordinationEvent({
        type: 'individual-to-bulk-sync',
        showId,
        timestamp: new Date(),
      });
    }

    this.metrics.coordinationEvents++;
    this.metrics.lastCoordination = new Date();
  }

  /**
   * Invalidate related caches when show data changes
   * Provides coordinated cache invalidation across related queries
   */
  public invalidateRelatedCaches(showId: string): Promise<void> {
    debugLog(`🗑️ Invalidating related caches for show ${showId}`);
    
    const wrapInvalidation = (options: Parameters<QueryClient['invalidateQueries']>[0]) => {
      return this.queryClient.invalidateQueries(options).catch(error => {
        debugError('Failed to invalidate cache:', options, error);
      });
    };

    const invalidationPromises: Promise<void | unknown>[] = [];

    // Invalidate individual show cache
    invalidationPromises.push(
      wrapInvalidation({
        queryKey: ['show', showId],
      }),
    );

    // Invalidate bulk shows cache
    invalidationPromises.push(
      wrapInvalidation({
        queryKey: SHOWS_QUERY_KEY,
      }),
    );

    // Invalidate any presenter-related caches if show has presenter info
    const individualShow = this.queryClient.getQueryData<Show>(['show', showId]);
    if (individualShow?.presenter) {
      invalidationPromises.push(
        wrapInvalidation({
          queryKey: ['presenters'],
        }),
      );
    }

    this.notifyCoordinationEvent({
      type: 'cache-invalidation',
      showId,
      timestamp: new Date(),
    });

    return Promise.all(invalidationPromises).then(() => {
      debugLog(`✅ Cache invalidation complete for show ${showId}`);
    });
  }

  /**
   * Prefetch individual show when bulk cache is populated
   * Optimizes cache hit rates by preloading likely-to-be-accessed shows
   */
  public prefetchPopularShows(shows: Show[], maxPrefetch: number = 5): void {
    debugLog(`🚀 Prefetching ${Math.min(maxPrefetch, shows.length)} popular shows`);
    
    // Sort shows by recency and prefetch the most recent ones
    const recentShows = [...shows] // Create copy to avoid mutating original
      .sort((a, b) => b.date_start_time.localeCompare(a.date_start_time))
      .slice(0, maxPrefetch);

    let prefetchedCount = 0;
    recentShows.forEach(show => {
      // Only prefetch if not already in individual cache
      const existingData = this.queryClient.getQueryData(['show', show.show_id]);
      if (!existingData) {
        this.queryClient.setQueryData(['show', show.show_id], show);
        prefetchedCount++;
        debugLog(`✅ Prefetched individual cache for show ${show.show_id}`);
      }
    });

    this.notifyCoordinationEvent({
      type: 'cache-prefetch',
      showCount: prefetchedCount,
      timestamp: new Date(),
    });
  }

  /**
   * Get cache coordination metrics
   */
  public getMetrics(): CacheCoordinationMetrics {
    return { ...this.metrics };
  }

  /**
   * Reset cache coordination metrics
   */
  public resetMetrics(): void {
    this.metrics = {
      bulkCacheHits: 0,
      individualCacheHits: 0,
      cacheMisses: 0,
      coordinationEvents: 0,
      lastCoordination: null,
    };
  }

  /**
   * Record cache hit for metrics
   */
  public recordCacheHit(type: 'bulk' | 'individual'): void {
    if (type === 'bulk') {
      this.metrics.bulkCacheHits++;
    } else {
      this.metrics.individualCacheHits++;
    }
  }

  /**
   * Record cache miss for metrics
   */
  public recordCacheMiss(): void {
    this.metrics.cacheMisses++;
  }

  /**
   * Add coordination event listener
   */
  public addCoordinationListener(listener: (event: CacheCoordinationEvent) => void): void {
    this.coordinationListeners.add(listener);
  }

  /**
   * Remove coordination event listener
   */
  public removeCoordinationListener(listener: (event: CacheCoordinationEvent) => void): void {
    this.coordinationListeners.delete(listener);
  }

  /**
   * Notify coordination event listeners
   */
  private notifyCoordinationEvent(event: CacheCoordinationEvent): void {
    this.coordinationListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        debugError('Error in coordination event listener:', error);
      }
    });
  }

  /**
   * Get cache consistency report
   * Analyzes consistency between individual and bulk caches
   */
  public getCacheConsistencyReport(): CacheConsistencyReport {
    const bulkShows = this.queryClient.getQueryData<Show[]>(SHOWS_QUERY_KEY);
    const cache = this.queryClient.getQueryCache();
    const individualShowQueries = cache.getAll().filter(query => 
      Array.isArray(query.queryKey) && 
      query.queryKey.length === 2 && 
      query.queryKey[0] === 'show'
    );

    const report: CacheConsistencyReport = {
      bulkCacheSize: bulkShows?.length || 0,
      individualCacheCount: individualShowQueries.length,
      consistentShows: 0,
      inconsistentShows: 0,
      orphanedIndividualCaches: 0,
      timestamp: new Date(),
    };

    if (bulkShows && Array.isArray(bulkShows) && bulkShows.length > 0) {
      // Bulk cache exists - compare with individual caches
      individualShowQueries.forEach(query => {
        const showId = query.queryKey[1] as string;
        const individualShow = query.state.data as Show;
        const bulkShow = bulkShows.find(show => show.show_id === showId);

        if (!bulkShow) {
          // Individual cache exists but show not in bulk cache
          report.orphanedIndividualCaches++;
        } else if (individualShow) {
          // Compare shows by stringifying to handle deep equality
          const individualStr = JSON.stringify(individualShow);
          const bulkStr = JSON.stringify(bulkShow);
          
          if (individualStr === bulkStr) {
            report.consistentShows++;
          } else {
            report.inconsistentShows++;
          }
        }
      });
    } else {
      // No bulk cache - all individual caches are orphaned
      report.orphanedIndividualCaches = individualShowQueries.length;
    }

    return report;
  }
}

// Event types for cache coordination
export interface CacheCoordinationEvent {
  type: 'bulk-to-individual-sync' | 'individual-to-bulk-sync' | 'cache-invalidation' | 'cache-prefetch';
  showId?: string;
  showCount?: number;
  timestamp: Date;
}

// Cache consistency report interface
export interface CacheConsistencyReport {
  bulkCacheSize: number;
  individualCacheCount: number;
  consistentShows: number;
  inconsistentShows: number;
  orphanedIndividualCaches: number;
  timestamp: Date;
}

// Create singleton instance
let cacheCoordinationService: CacheCoordinationService | null = null;

export const getCacheCoordinationService = (queryClient: QueryClient): CacheCoordinationService => {
  if (!cacheCoordinationService) {
    cacheCoordinationService = new CacheCoordinationService(queryClient);
  }
  return cacheCoordinationService;
};
